import { cx } from "cva";
import { Fragment } from "react";
import { AssetDownload, AssetDownloadProps } from "~/components/AssetDownload";
import { AuthorBio, AuthorBioProps } from "~/components/AuthorBio";
import { BuildHtml } from "~/components/BuildHtml";
import { ContentPromo, ContentPromoProps } from "~/components/ContentPromo";
import {
  KeyTakeawaysCard,
  KeyTakeawaysCardProps,
} from "~/components/KeyTakeawaysCard";
import { LandingPageContainer } from "~/components/LandingPageV2/components/LandingPageContainer";
import { LandingPageContent } from "~/components/LandingPageV2/components/LandingPageContent";
import { PostEditCTA, PostEditCTAProps } from "~/components/PostEditCTA";
import { ReportPreviewContentBlock } from "~/components/ReportPreviewContentBlocks/ReportPreviewContentBlock";
import {
  ReportPreviewContentSummary,
  ReportPreviewContentSummaryProps,
} from "~/components/ReportPreviewContentSummary";
import {
  ReportPreviewDownloadButton,
  ReportPreviewDownloadButtonProps,
} from "~/components/ReportPreviewDownloadButton";
import {
  ReportPreviewHero,
  ReportPreviewHeroProps,
} from "~/components/ReportPreviewHero";
import { Sidecar } from "~/components/Sidecar/Sidecar";
import { SidecarBlock } from "~/components/Sidecar/SidecarBlock";
import { useSidecarProps } from "~/components/Sidecar/useSidecarProps";
import { Tags, TagsProps } from "~/components/Tags";
import { TagToClassNameMap } from "~/lib/shared/html-utils";
import { PostContentBlock } from "~/types/asgard/PostContentBlock";
import { Body, Headline } from "~/ui-library";
import {
  ReportPreviewHeader,
  ReportPreviewHeaderProps,
} from "../ReportPreviewHeader";

export type ReportPreviewPageProps = {
  assetDownloadProps: AssetDownloadProps | null;
  authorBiosProps: AuthorBioProps[] | null;
  contentBlocks: PostContentBlock[];
  contentSummaryProps: Pick<ReportPreviewContentSummaryProps, "contentHtml">;
  downloadButtonProps: ReportPreviewDownloadButtonProps;
  keyTakeawaysProps: KeyTakeawaysCardProps | null;
  downloadDescription: string | null;
  heroProps: ReportPreviewHeroProps;
  methodology: string | null;
  contentPromoProps: Pick<ContentPromoProps, "posts"> | null;
  reportHeaderProps: ReportPreviewHeaderProps;
  summary: string;
  tagsProps: TagsProps | null;
  shareUrl: string;
  postId: number;
  postEditCTAProps: PostEditCTAProps;
  enableSidecar?: boolean;
  preview?: boolean;
};

const sharedHtmlClassNameMap: TagToClassNameMap = new Map([
  ["p", "mb-m last:mb-0"],
  ["ul", "list-disc pl-m"],
  ["li", "mb-s last:mb-0"],
]);

export function getAuthorsSectionHeadline(authorList: AuthorBioProps[]) {
  if (authorList.length > 1) {
    return "About the authors";
  }
  return "About the author";
}

const AuthorsSection = ({
  authorBiosProps,
}: Pick<ReportPreviewPageProps, "authorBiosProps">) => (
  <div>
    <Headline as="h3" className="mb-s" level={3}>
      {authorBiosProps
        ? getAuthorsSectionHeadline(authorBiosProps)
        : "About the authors"}
    </Headline>

    <div className="flex flex-col gap-m">
      {authorBiosProps
        ? authorBiosProps.map(({ ...authorProps }, idx) => (
            <AuthorBio {...authorProps} key={idx} />
          ))
        : null}
    </div>
  </div>
);

const DownloadBlock = ({
  attachmentId,
  postId,
  dataAccess,
  downloadDescription,
}: ReportPreviewDownloadButtonProps & { downloadDescription: string }) => (
  <div className="flex flex-col">
    <Headline as="h3" className="mb-s" level={3}>
      Download this report
    </Headline>

    <Body className="mb-m">{downloadDescription}</Body>

    <div className="w-fit">
      <ReportPreviewDownloadButton
        attachmentId={attachmentId}
        postId={postId}
        dataAccess={dataAccess}
      />
    </div>
  </div>
);

const MethodologyBlock = ({
  methodology,
}: Pick<ReportPreviewPageProps, "methodology">) => (
  <div className="flex flex-col">
    <Headline as="h3" className="mb-s" level={3}>
      Methodology
    </Headline>
    <Body size="large">
      <BuildHtml
        tagToClassNameMap={sharedHtmlClassNameMap}
        html={methodology}
      />
    </Body>
  </div>
);

/** Display component to be used for the analyst report preview page */
export const ReportPreviewPage: React.FC<ReportPreviewPageProps> = ({
  assetDownloadProps,
  authorBiosProps,
  contentBlocks,
  contentSummaryProps,
  downloadButtonProps,
  downloadDescription,
  heroProps,
  keyTakeawaysProps,
  methodology,
  contentPromoProps,
  reportHeaderProps,
  summary,
  tagsProps,
  shareUrl,
  postId,
  postEditCTAProps,
  enableSidecar,
  preview,
}) => {
  const {
    isLoadingSidecar,
    isValidatingSidecar,
    showDesktopSidecar,
    sidecarBlocks,
    keyTopicsBlock,
    postListBlocks,
    relatedReportBlock,
    wysiwygBlocks,
  } = useSidecarProps({ postId, preview });

  return (
    <>
      <PostEditCTA {...postEditCTAProps} />
      <div className="flex w-full justify-center">
        <article className="flex size-full min-h-screen flex-col items-center pb-xl">
          <div className="flex w-full items-center">
            <div className="relative flex w-full flex-col items-center">
              <ReportPreviewHero {...heroProps} />

              <LandingPageContent
                maxWidth="medium"
                className="z-20 mb-xl pt-s md:pt-m lg:min-h-[525px] lg:bg-transparent lg:pt-l"
              >
                <div>
                  <ReportPreviewHeader
                    {...reportHeaderProps}
                    className="relative lg:pb-l lg:pt-xl"
                  />
                </div>
              </LandingPageContent>
            </div>
          </div>
          {/* Shift article contents into hero image */}
          <div className="z-20 flex w-full justify-center">
            <div
              className={cx(
                "relative",
                "bg-white",
                "-top-s lg:-top-xl",
                "w-fit",
                "pt-s md:max-w-[1024px] md:pt-m"
              )}
            >
              <div className="flex flex-col items-center">
                <div
                  className={[
                    "flex flex-col-reverse justify-center md:flex-row",
                  ].join(" ")}
                >
                  <LandingPageContainer
                    topPadding="none"
                    bottomPadding="none"
                    sidecar={
                      enableSidecar &&
                      showDesktopSidecar && (
                        <Sidecar
                          blocks={sidecarBlocks}
                          isLoading={isLoadingSidecar}
                          isValidating={isValidatingSidecar}
                          data-testid="report-sidecar"
                        />
                      )
                    }
                  >
                    <LandingPageContent maxWidth="memo" className="mb-xl">
                      <Headline as="h2" className="mb-m" level={2}>
                        Report summary
                      </Headline>
                      <Body size="large">
                        <BuildHtml
                          html={summary}
                          tagToClassNameMap={sharedHtmlClassNameMap}
                        />
                      </Body>
                    </LandingPageContent>

                    {keyTakeawaysProps ? (
                      <LandingPageContent maxWidth="memo" className="mb-xl">
                        <KeyTakeawaysCard {...keyTakeawaysProps} />
                      </LandingPageContent>
                    ) : null}

                    {!!(
                      enableSidecar &&
                      !showDesktopSidecar &&
                      relatedReportBlock
                    ) && (
                      <LandingPageContent
                        as="section"
                        maxWidth="memo"
                        className="my-l"
                        data-testid="sidecar-related-report-mobile"
                      >
                        <Sidecar
                          isLoading={isLoadingSidecar}
                          isValidating={isValidatingSidecar}
                          blocks={[relatedReportBlock]}
                        />
                      </LandingPageContent>
                    )}

                    {assetDownloadProps ? (
                      <LandingPageContent
                        maxWidth="memo"
                        className="mb-xl"
                        data-testid="asset-download"
                      >
                        <AssetDownload {...assetDownloadProps} />
                      </LandingPageContent>
                    ) : null}

                    {contentBlocks?.length ? (
                      <LandingPageContent maxWidth="memo" className="mb-s pb-m">
                        <Headline as="h3" level={3}>
                          Highlights from this report
                        </Headline>
                      </LandingPageContent>
                    ) : null}

                    {contentBlocks?.length
                      ? contentBlocks.map((contentBlock) => (
                          <Fragment key={postId}>
                            <LandingPageContent
                              maxWidth="memo"
                              className="mb-xl"
                            >
                              <ReportPreviewContentBlock
                                contentBlock={contentBlock}
                              />
                            </LandingPageContent>
                          </Fragment>
                        ))
                      : null}

                    {downloadDescription ? (
                      <LandingPageContent maxWidth="memo" className="mb-xl">
                        <DownloadBlock
                          downloadDescription={downloadDescription}
                          {...downloadButtonProps}
                        />
                      </LandingPageContent>
                    ) : null}

                    {methodology ? (
                      <LandingPageContent maxWidth="memo" className="mb-xl">
                        <MethodologyBlock methodology={methodology} />
                      </LandingPageContent>
                    ) : null}

                    {!!(
                      enableSidecar &&
                      !showDesktopSidecar &&
                      keyTopicsBlock
                    ) && (
                      <LandingPageContent
                        as="section"
                        maxWidth="memo"
                        className="my-xl"
                        data-testid="sidecar-keytopics-mobile"
                      >
                        <SidecarBlock
                          border={false}
                          gutters={false}
                          block={keyTopicsBlock}
                          blockIndex={0}
                        />
                      </LandingPageContent>
                    )}

                    <LandingPageContent maxWidth="memo" className="mb-xl">
                      <AuthorsSection authorBiosProps={authorBiosProps} />
                    </LandingPageContent>

                    {!!(
                      enableSidecar &&
                      !showDesktopSidecar &&
                      wysiwygBlocks?.length
                    ) &&
                      wysiwygBlocks.map((wysiwygBlock, idx) => (
                        <LandingPageContent
                          as="section"
                          maxWidth="memo"
                          className="mb-l"
                          data-testid="sidecar-wysiwyg-mobile"
                          key={`${wysiwygBlock.acfFcLayout}-${idx}`}
                        >
                          <SidecarBlock
                            border={false}
                            gutters={false}
                            block={wysiwygBlock}
                            blockIndex={0}
                          />
                        </LandingPageContent>
                      ))}
                    {!!(
                      enableSidecar &&
                      !showDesktopSidecar &&
                      postListBlocks?.length
                    ) &&
                      postListBlocks.map((postListBlock, idx) => (
                        <LandingPageContent
                          as="section"
                          maxWidth="memo"
                          className="mb-l last:mb-0"
                          data-testid="sidecar-postlist-mobile"
                          key={`${postListBlock.acfFcLayout}-${idx}`}
                        >
                          <SidecarBlock
                            border={false}
                            gutters={false}
                            block={postListBlock}
                            blockIndex={0}
                          />
                        </LandingPageContent>
                      ))}
                  </LandingPageContainer>

                  {!enableSidecar && (
                    <div className="relative" data-testid="legacy-sidecar">
                      <aside className="sticky top-[128px] mb-xl flex grow flex-col px-xs  md:mt-s md:min-w-[344px] md:pl-0 md:pr-s lg:top-[64px] lg:min-w-[344px] lg:max-w-[344px] lg:pr-m lg:pt-xl">
                        <ReportPreviewContentSummary
                          title="About this report"
                          {...contentSummaryProps}
                          shareTitle={reportHeaderProps.headline}
                          shareUrl={shareUrl}
                          downloadButtonProps={downloadButtonProps}
                        />
                        {tagsProps ? (
                          <div className="border-1 border-t-0 p-m">
                            <Headline as="h5" className="mb-s" level={5}>
                              Key Topics
                            </Headline>
                            <Tags {...tagsProps} />
                          </div>
                        ) : null}
                      </aside>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {contentPromoProps ? (
            <LandingPageContent maxWidth="medium">
              <ContentPromo
                headline={
                  <Headline as="h3" level={3}>
                    Explore more
                  </Headline>
                }
                {...contentPromoProps}
              />
            </LandingPageContent>
          ) : null}
        </article>
      </div>
    </>
  );
};
